'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import {
  ArrowUpRight, Award, Briefcase, ChevronRight, Coins, LayoutGrid,
  LineChart, Loader2, Puzzle, Sparkles,
  Target, TrendingUp, Users, MessageSquare,
  FileSearch, Crosshair,
} from 'lucide-react'
import MarketingNav from '@/components/marketing/Nav'
import MarketingFooter from '@/components/marketing/Footer'
import HeroSignalFeed from '@/components/marketing/HeroSignalFeed'
import MockDashboard from '@/components/marketing/MockDashboard'
import WatchlistDemo from '@/components/marketing/WatchlistDemo'
// GlobeSection ships ~38 KB gzip of world-atlas topology on top of the
// d3-geo runtime. The section sits below the fold, so we lazy-load it
// via next/dynamic with ssr:false to keep it out of initial JS. The
// loading placeholder reserves the layout slot to prevent CLS.
const GlobeSection = dynamic(
  () => import('@/components/marketing/GlobeSection'),
  {
    ssr: false,
    loading: () => (
      <div className="relative grid grid-cols-1 lg:grid-cols-12 items-center gap-8 lg:gap-12">
        <div className="lg:col-span-7 h-[440px] sm:h-[540px] lg:h-[620px] rounded-2xl bg-cream-50/60 animate-pulse" />
        <div className="lg:col-span-5 h-[300px] rounded-2xl bg-cream-50/60 animate-pulse" />
      </div>
    ),
  },
)
import { createCheckout, type CheckoutPlan } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth'
import { useToast } from '@/components/ui/Toast'
import { jsonLdProps } from '@/lib/jsonLd'

export default function HomeClient() {
  const router = useRouter()
  const toast = useToast()
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual')
  const [pendingPlan, setPendingPlan] = useState<CheckoutPlan | null>(null)

  async function handleCheckout(plan: CheckoutPlan) {
    if (!isAuthenticated()) {
      router.push(`/signup?plan=${plan}&billing=${billing}`)
      return
    }
    setPendingPlan(plan)
    try {
      const { checkout_url } = await createCheckout(plan, billing)
      window.location.href = checkout_url
    } catch {
      // The user is already signed in, so bouncing them to /signup made a
      // checkout error look like they needed to register again. Surface the
      // real failure and keep them on the page so they can retry.
      toast.error('We could not start your checkout. Please try again in a moment.')
    } finally {
      setPendingPlan(null)
    }
  }

  return (
    <main className="bg-cream-100 text-ink-900 overflow-hidden relative">
      {/* FAQPage structured data, mirroring /pricing's pattern: Google can
          surface rich expandable FAQ results from the homepage FAQ block. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={jsonLdProps(FAQ_PAGE_LD)}
      />
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] rounded-full blur-3xl bg-terra-100/40" />
        <div className="absolute top-[40%] right-[-200px] w-[600px] h-[600px] rounded-full blur-3xl bg-olive-100/40" />
        <div className="absolute top-[78%] left-[-100px] w-[700px] h-[700px] rounded-full blur-3xl bg-petrol-100/30" />
      </div>

      <MarketingNav />

      <div className="editorial relative">
        {/* Hero */}
        <section className="pt-32 sm:pt-40 pb-12 sm:pb-16">
          <div className="inline-flex items-center gap-3 mb-8 sm:mb-10">
            <span className="signal-dot" />
            <span className="text-[14px] sm:text-[15px] font-semibold uppercase tracking-[0.18em] text-olive-700">
              Competitive intelligence for SaaS
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-7">
              <h1 className="text-h-xl font-semibold tracking-[-0.035em] leading-[0.98] text-ink-900 text-balance">
                Track every move your{' '}
                <span className="text-terra-500">competitors</span>{' '}
                make.
              </h1>
              <p className="mt-5 sm:mt-6 text-h-sm font-semibold tracking-tight text-ink-700 text-balance leading-snug">
                Bring your watchlist on Tuesday.{' '}
                <span className="text-terra-600">Brief the board on Wednesday.</span>
              </p>
              <p className="lede mt-7 sm:mt-9 text-pretty text-ink-700">
                PulseSignal watches the public signals every competitor leaves behind: pricing,
                hiring, funding, leadership, product launches, sentiment, tech stack, and positioning.
                We refresh every day and send only the changes that matter, straight to your inbox,
                Slack, or Telegram.
              </p>

              <div className="mt-9 sm:mt-11 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link href="/signup" className="btn-primary text-[16px] px-6 py-3.5">
                  Start 14-day trial
                  <ArrowUpRight className="w-5 h-5" />
                </Link>
                <Link href="#how" className="btn-ghost text-[16px] px-6 py-3.5">
                  See how it works
                </Link>
              </div>

              <p className="mt-5 sm:mt-6 text-[14px] text-ink-600">
                14-day trial. Cancel anytime.
              </p>
            </div>

            <div className="lg:col-span-5">
              <HeroSignalFeed />
              <p className="mt-5 text-[14px] font-semibold uppercase tracking-widest text-ink-700 text-center">
                Sample · yours fills with your watchlist
              </p>
            </div>
          </div>
        </section>

        {/* How it works, 3 steps so a cold visitor gets the loop in 10 seconds */}
        <section id="how" className="py-14 sm:py-20 scroll-mt-24">
          <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 text-balance leading-[1.05] max-w-3xl">
            How it works. <span className="text-petrol-500">Three steps, ninety seconds.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10 sm:mt-14">
            {[
              {
                n: '01',
                t: 'Add your competitors',
                b: 'Type their domains. No integrations to configure. Most users finish set-up in under 90 seconds.',
              },
              {
                n: '02',
                t: 'We check every day',
                b: 'One full cycle a day, at 02:00 UTC. We watch pricing pages, hiring boards, leadership profiles, and funding news.',
              },
              {
                n: '03',
                t: 'Only the changes that matter',
                b: 'Severity-coded so only critical and notable signals page you. Daily digest groups the rest. No noise, no FOMO alerts.',
              },
            ].map((s) => (
              <article key={s.n} className="card-interactive bg-cream-50 border border-ink-900/10 rounded-2xl p-6 sm:p-7 flex flex-col">
                <p className="numeral-step tabular-nums mb-4">{s.n}</p>
                <h3 className="text-h-sm font-semibold tracking-tight text-ink-900">{s.t}</h3>
                <p className="mt-3 text-body-sm text-ink-700 leading-relaxed">{s.b}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Modules, what we capture, framed as the operator's job */}
        <section id="modules" className="py-14 sm:py-20 scroll-mt-24">
          <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 text-balance leading-[1.05] max-w-3xl">
            Ten signal modules, <span className="text-olive-600">captured every day.</span>
          </h2>
          <p className="lede mt-5 max-w-2xl text-ink-700">
            Every tracked competitor runs through ten signal modules: pricing, leadership, hiring,
            API and integrations, product launches, customer logos, customer sentiment, tech stack,
            positioning, and funding. Each one has its own dashboard, and every change is
            severity-coded and linked back to its source, so a digest reads in under a minute.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10 sm:mt-14">
            <BentoCard icon={LineChart}    title="Pricing"           body="Plan changes, page diffs, version history. The first place to look when a deal slips." accent="terra"  />
            <BentoCard icon={Briefcase}    title="Leadership"        body="VP movement. Founder departures. The signal that predicts everything else." accent="terra"  />
            <BentoCard icon={Users}        title="Hiring"            body="Engineering surges. Roles that betray a roadmap before launch." accent="petrol" />
            <BentoCard icon={Puzzle}       title="API and integrations" body="New connectors, API docs, partner listings. The ecosystem they are building toward." accent="petrol" />
            <BentoCard icon={Sparkles}     title="Product launches"  body="Changelog deltas. Beta announcements. The moment a feature ships." accent="petrol" />
            <BentoCard icon={Award}        title="Customer logos"    body="New customer wins and logos added or dropped. Who they are landing, and who they are losing." accent="olive"  />
            <BentoCard icon={MessageSquare} title="Customer sentiment" body="Trustpilot / public app stores, review velocity. When customers leave, you see it first." accent="olive"  />
            <BentoCard icon={LayoutGrid}   title="Tech stack"        body="Vendors added or dropped. The infra story behind their product." accent="olive"  />
            <BentoCard icon={Target}       title="Positioning"       body="Hero rewrites. Tagline shifts. The goalposts moving on how they sell." accent="terra"  />
            <BentoCard icon={Coins}        title="Funding"           body="Series A to IPO. Round size. Lead investor. The board call before the announcement." accent="olive"  />
          </div>
        </section>

        {/* Workflows that read ACROSS the ten modules. Acquisition scoring and
            due diligence are WORKFLOWS, not counted modules. Market Map is a
            dashboard VIEW, also not counted, and lives in the "What you see on
            day one" section, matching the canonical module taxonomy. */}
        <section className="py-14 sm:py-20">
          <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 text-balance leading-[1.05] max-w-3xl">
            Two workflows that <span className="text-petrol-500">act on the ten signals.</span>
          </h2>
          <p className="lede mt-5 max-w-2xl text-ink-700">
            Acquisition scoring and due diligence are not extra modules. They are workflows that
            read across all ten signal modules and hand you the artifact you actually present.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10 sm:mt-14 max-w-3xl">
            <article className="card-interactive bg-cream-50 border border-ink-900/10 rounded-2xl p-6 sm:p-7 flex flex-col">
              <FileSearch className="w-6 h-6 text-terra-500" strokeWidth={1.5} />
              <h3 className="text-h-sm font-semibold tracking-tight text-ink-900 mt-5">Due-diligence reports</h3>
              <p className="mt-3 text-body-sm text-ink-700 leading-relaxed">
                A board-ready report on any company in your watchlist. Public-source-backed, live
                citations, generated in under a minute. Pro gets 10 a month, Business 50, Scale unlimited.
              </p>
            </article>
            <article className="card-interactive bg-cream-50 border border-ink-900/10 rounded-2xl p-6 sm:p-7 flex flex-col">
              <Crosshair className="w-6 h-6 text-olive-600" strokeWidth={1.5} />
              <h3 className="text-h-sm font-semibold tracking-tight text-ink-900 mt-5">Acquisition Targets</h3>
              <p className="mt-3 text-body-sm text-ink-700 leading-relaxed">
                A ranked candidate list for when you&apos;re buying, or being bought. Recomputed weekly
                from the signal graph and the public M&amp;A history we already capture.
              </p>
            </article>
          </div>
        </section>

        {/* Global coverage: rotating Earth + tracked-city pings. Tells
            the worldwide-coverage story (with the honest index counts)
            before the dashboard preview. */}
        <section id="coverage" className="py-14 sm:py-20 scroll-mt-24">
          <GlobeSection />
        </section>

        {/* Live dashboard preview, show the real product layout right after the module grid */}
        <section className="py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 leading-[1.05] text-balance">
                What you see <span className="text-terra-500">on day one.</span>
              </h2>
              <p className="lede mt-5 text-ink-700">
                One feed, severity-coded. Drill down to the source change in two clicks, map your
                competitive set, then route any change into Slack, your inbox, or any tool by webhook.
              </p>
              <Link href="/signup" className="btn-primary mt-7 text-[16px] px-6 py-3.5">
                Tour the live product
                <ArrowUpRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="lg:col-span-8">
              <MockDashboard />
            </div>
          </div>
        </section>

        {/* Watchlist input */}
        <section className="py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Mobile order is flipped so the section header + copy
                renders BEFORE the graphic on mobile. Desktop layout (text
                on the left, graphic on the right) is preserved via the
                `lg:` overrides. */}
            <div className="lg:col-span-5 order-1">
              <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 leading-[1.05] text-balance">
                Bring your watchlist.{' '}
                <span className="text-olive-600">Walk in tomorrow knowing more</span>{' '}
                than your competitors.
              </h2>
              <p className="lede mt-6 text-ink-700">
                Type their domains. We index pricing pages, hiring boards, leadership profiles,
                changelogs, sentiment feeds, fundraising news. Every signal module, every day.
              </p>
              <p className="mt-5 text-[14px] uppercase tracking-widest text-ink-600">
                Setup time · 90 seconds. No integrations to configure.
              </p>
            </div>
            <div className="lg:col-span-7 order-2">
              <WatchlistDemo />
            </div>
          </div>
        </section>

        {/* Honest stats band, three numbers that mean something */}
        <section className="py-10 sm:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 text-center py-4">
            {[
              { k: '10',     l: 'Signal modules' },
              { k: 'Daily',  l: 'Refresh cadence' },
              { k: '90 sec', l: 'To set up your watchlist' },
            ].map((s, i) => (
              <div key={i}>
                <p className="numeral-stat text-ink-900">{s.k}</p>
                <p className="mt-4 text-[13px] sm:text-[14px] uppercase tracking-widest text-ink-700 font-medium">
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Sources & channels, compressed: example chips + see-all link */}
        <section className="py-14 sm:py-20">
          <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 leading-[1.05] text-balance max-w-3xl">
            Public sources go in. <span className="text-petrol-500">Your channels come out.</span>
          </h2>
          <p className="lede mt-5 max-w-2xl text-ink-700">
            Public, terms-of-service-friendly sources only. No scraping of authenticated content.
            No LinkedIn, no Crunchbase, nothing whose terms restrict indexing.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10 sm:mt-14">
            <article className="card-interactive bg-cream-50 border border-ink-900/10 rounded-2xl p-6 sm:p-7">
              <p className="text-eyebrow uppercase tracking-widest text-terra-700 font-semibold">
                Sources we index
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  'SEC EDGAR',
                  'Companies House',
                  'Greenhouse / Lever ATS',
                  'Trustpilot · App Store · Google Play',
                  'Wayback Machine',
                ].map((s) => (
                  <span key={s} className="inline-flex items-center text-[13px] px-2.5 py-1 rounded-full border border-ink-900/10 bg-cream-100 text-ink-800">
                    {s}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-[14px] text-ink-600">
                The full list of sources we index is in the{' '}
                <Link href="/privacy/sub-processors" className="text-terra-700 hover:text-terra-600 underline underline-offset-2">
                  sub-processor list
                </Link>
                , kept current as we add them.
              </p>
            </article>
            <article className="card-interactive bg-cream-50 border border-ink-900/10 rounded-2xl p-6 sm:p-7">
              <p className="text-eyebrow uppercase tracking-widest text-olive-600 font-semibold">
                Channels we route to
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  'Daily email digest',
                  'Slack',
                  'Microsoft Teams',
                  'Telegram',
                  'Webhooks',
                  'CSV / JSON export',
                  'API',
                  'PDF reports',
                ].map((c) => (
                  <span key={c} className="inline-flex items-center text-[13px] px-2.5 py-1 rounded-full border border-ink-900/10 bg-cream-100 text-ink-800">
                    {c}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-[14px] text-ink-600">
                Severity-coded so only critical and notable signals page you. Info-level groups
                into the daily digest. No noise, no FOMO alerts.
              </p>
            </article>
          </div>
        </section>

        {/* Use cases */}
        <section id="use-cases" className="py-14 sm:py-20 scroll-mt-24">
          <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 text-balance leading-[1.05] max-w-3xl">
            Every role. <span className="text-olive-600">One feed.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10 sm:mt-14">
            {USE_CASES.map((u) => (
              <article
                key={u.role}
                className="card-interactive bg-cream-50 border border-ink-900/10 rounded-2xl p-6 sm:p-7 flex flex-col"
              >
                <u.icon className="w-6 h-6 text-terra-500" strokeWidth={1.5} />
                <h3 className="text-h-sm font-semibold tracking-tight text-ink-900 mt-5">
                  {u.role}
                </h3>
                <p className="mt-3 text-body text-ink-700 leading-relaxed">
                  {u.body}
                </p>
                <ul className="mt-6 space-y-2.5 border-t border-ink-900/10 pt-5">
                  {u.uses.map((use) => (
                    <li key={use} className="flex items-start gap-2.5 text-body-sm text-ink-700">
                      <ChevronRight className="w-4 h-4 text-terra-500 mt-1 shrink-0" />
                      <span>{use}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-14 sm:py-20 scroll-mt-24">
          <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 text-balance leading-[1.05] max-w-3xl">
            Priced for <span className="text-terra-500">the cost of being late.</span>
          </h2>
          <p className="lede mt-5 max-w-2xl text-ink-700">
            One missed pricing change costs more than a year of Pro. Start a 14-day trial on any
            tier and keep the plan that earns its keep.
          </p>

          {/* Toggle ABOVE the cards */}
          <div className="mt-8 inline-flex items-center gap-1 bg-cream-50 border border-ink-900/10 rounded-full p-1">
            <button
              onClick={() => setBilling('monthly')}
              className={
                'px-5 py-2 rounded-full text-[14px] font-medium transition-colors ' +
                (billing === 'monthly'
                  ? 'bg-terra-600 text-cream-50'
                  : 'text-ink-700 hover:text-ink-900')
              }
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('annual')}
              className={
                'px-5 py-2 rounded-full text-[14px] font-medium transition-colors flex items-center gap-2 ' +
                (billing === 'annual'
                  ? 'bg-terra-600 text-cream-50'
                  : 'text-ink-700 hover:text-ink-900')
              }
            >
              Annual
              <span
                className={
                  'text-[12px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 ' +
                  (billing === 'annual'
                    ? 'bg-cream-50 text-terra-700'
                    : 'bg-olive-100 text-olive-700')
                }
              >
                2 months free
              </span>
            </button>
          </div>

          {/* Which tier are you: a quick persona-to-tier read so a visitor
              self-selects before scanning four feature lists. Non-interactive
              guidance; the cards below carry the detail + the Recommended flag. */}
          <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[13px] text-ink-700">
            <span className="text-ink-600">Which tier are you?</span>
            {[
              ['Running intel solo', 'Starter'],
              ['Team losing deals', 'Pro'],
              ['Briefing the board weekly', 'Business'],
              ['Intel across the org', 'Scale'],
            ].map(([persona, tier], i) => (
              <span key={tier} className="inline-flex items-center gap-2">
                {i > 0 && <span className="text-ink-300" aria-hidden>·</span>}
                <span>
                  {persona}{' '}
                  <span className="font-semibold text-terra-700">{tier}</span>
                </span>
              </span>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mt-8 sm:mt-10">
            {PRICING.map((p) => {
              const price = billing === 'annual' ? p.annualPerMonth : p.monthly
              return (
                <article
                  key={p.name}
                  className={
                    'card-interactive bg-cream-50 border rounded-2xl p-6 flex flex-col ' +
                    (p.featured
                      ? 'border-terra-400/50 ring-1 ring-terra-400/30 bg-terra-50/40'
                      : 'border-ink-900/10 hover:border-ink-900/20')
                  }
                >
                  <div className="flex items-center justify-between">
                    <p className="text-eyebrow font-semibold uppercase tracking-widest text-ink-600">
                      {p.name}
                    </p>
                    {p.featured && (
                      <span className="text-[12px] uppercase tracking-widest text-terra-700 font-semibold">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="mt-5 leading-none">
                    <span className="text-h-lg font-semibold tracking-tight text-ink-900">${price.toLocaleString()}</span>
                    {price > 0 && <span className="text-h-sm text-ink-600 ml-1">/mo</span>}
                  </p>
                  {billing === 'annual' && p.monthly > 0 ? (
                    <p className="text-[14px] text-ink-600 mt-2">
                      Billed ${p.annualTotal.toLocaleString()}/yr
                    </p>
                  ) : (
                    <p className="text-[14px] text-ink-600 mt-2">{p.tagline}</p>
                  )}
                  <ul className="mt-6 space-y-2 border-t border-ink-900/10 pt-5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-body-sm text-ink-700">
                        <ChevronRight className="w-4 h-4 text-terra-500 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => handleCheckout(p.slug)}
                    disabled={pendingPlan === p.slug}
                    className={'mt-6 ' + (p.featured ? 'btn-primary' : 'btn-ghost') + ' disabled:opacity-60'}
                  >
                    {pendingPlan === p.slug ? (
                      <>
                        Loading...
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </>
                    ) : (
                      <>
                        {p.ctaLabel}
                        <ArrowUpRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </article>
              )
            })}
          </div>

          <div className="flex items-center justify-between mt-7 flex-wrap gap-3">
            <p className="text-[14px] uppercase tracking-widest text-ink-600">
              14-day trial · No charge until day 15 · Cancel anytime
            </p>
            <Link href="/pricing" className="text-[14px] text-terra-700 hover:text-terra-600 inline-flex items-center gap-1">
              See full comparison
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* FAQs */}
        <section id="faq" className="py-14 sm:py-20 scroll-mt-24">
          <div className="max-w-3xl">
            <h2 className="text-h-lg font-semibold tracking-[-0.025em] text-ink-900 leading-[1.05] text-balance">
              FAQs <span className="text-petrol-500">worth asking.</span>
            </h2>
            <p className="lede mt-5 text-ink-700">
              The ones we hear most often from operators evaluating us.
            </p>
          </div>
          <div className="mt-10 sm:mt-12 divide-y divide-ink-900/10 max-w-4xl">
            {FAQS.map((f, i) => (
              <details key={i} name="home-faq" className="group py-2 first:pt-0 last:pb-0">
                <summary className="summary-hover flex items-start justify-between gap-6 cursor-pointer list-none px-4 py-5 transition-colors hover:[&_h3]:text-terra-500 hover:[&_.faq-plus]:text-terra-500 focus-visible:outline-none focus-visible:[&_h3]:text-terra-500">
                  <h3 className="text-h-sm font-semibold tracking-tight text-ink-900 pr-6 transition-colors duration-200">
                    {f.q}
                  </h3>
                  <span className="faq-plus text-h-sm text-ink-600 group-open:text-terra-500 transition-colors duration-200 shrink-0 mt-0.5">
                    <span className="group-open:hidden">+</span>
                    <span className="hidden group-open:inline">−</span>
                  </span>
                </summary>
                <p className="mt-4 px-4 text-body text-ink-700 leading-relaxed max-w-3xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>

      </div>

      <MarketingFooter />
    </main>
  )
}

/* Bento card */

const ACCENT_TINT = {
  terra:  'text-terra-500',
  olive:  'text-olive-600',
  petrol: 'text-petrol-500',
} as const

function BentoCard({
  icon: Icon, title, body, accent = 'terra',
}: {
  icon: React.ElementType; title: string; body: string;
  accent?: 'terra' | 'olive' | 'petrol'
}) {
  return (
    <article className="card-interactive bg-cream-50 border border-ink-900/10 rounded-2xl p-6 flex flex-col group min-h-[200px]">
      <Icon className={'w-6 h-6 ' + ACCENT_TINT[accent]} strokeWidth={1.5} />
      <h3 className="text-h-sm font-semibold tracking-tight text-ink-900 mt-5 leading-tight">
        {title}
      </h3>
      <p className="mt-2 text-body-sm text-ink-700 leading-relaxed">
        {body}
      </p>
    </article>
  )
}

const USE_CASES: { role: string; icon: React.ElementType; body: string; uses: string[] }[] = [
  {
    role: 'Founders & CEOs',
    icon: Target,
    body: 'Walk into the board meeting knowing your top three competitors\' moves before they do, and one frame ahead of the question.',
    uses: [
      'Daily digest, your watchlist only',
      'Board-ready due-diligence PDF',
      'Telegram + email severity routing',
    ],
  },
  {
    role: 'Growth & Marketing',
    icon: TrendingUp,
    body: 'Every pricing shift, every positioning rewrite, indexed. Your competitive matrix updates itself.',
    uses: [
      'Pricing page change alerts on the same day',
      'Hero & messaging snapshot history',
      'Sentiment & review velocity per cohort',
    ],
  },
  {
    role: 'Sales & RevOps',
    icon: Users,
    body: 'Open the deal page, see the prospect\'s competitive set move on the same day. The objection they\'re about to raise? You knew yesterday.',
    uses: [
      'Per-account signal feed in Slack',
      'Battlecard pulled from live signals',
      'Deal-room briefings via Slack/Teams',
    ],
  },
]

// Canonical 5-tier model (B0). Mirrors src/app/pricing/plans.ts and
// backend/app/core/plans.py PLAN_CONFIG. Keep prices in sync.
const PRICING: {
  name: string;
  slug: CheckoutPlan;
  monthly: number;
  annualPerMonth: number;
  annualTotal: number;
  tagline: string;
  features: string[];
  featured?: boolean;
  ctaLabel: string;
}[] = [
  {
    name: 'Starter',
    slug: 'starter',
    monthly: 199,
    annualPerMonth: 166,
    annualTotal: 1990,
    tagline: 'For the operator running competitive intel solo.',
    features: [
      '20 tracked companies',
      '6-month signal history',
      'Daily digest + same-day critical alerts',
      'All signal modules',
      '1 user',
      'CSV export',
    ],
    ctaLabel: 'Start trial',
  },
  {
    name: 'Pro',
    slug: 'pro',
    monthly: 549,
    annualPerMonth: 458,
    annualTotal: 5490,
    tagline: 'For teams losing deals to information asymmetry.',
    featured: false,
    features: [
      '60 tracked companies',
      '12-month signal history',
      'Slack + Microsoft Teams + Telegram + email',
      '5 users',
      'Custom alert rules + webhooks',
      '10 due-diligence reports / mo',
      '10 auto-battlecards / mo',
      'API access (10K req/day)',
    ],
    ctaLabel: 'Start trial',
  },
  {
    name: 'Business',
    slug: 'business',
    monthly: 1299,
    annualPerMonth: 1083,
    annualTotal: 12990,
    tagline: 'For teams that are done flying blind.',
    featured: true,
    features: [
      'Everything in Pro',
      '200 tracked companies',
      '24-month signal history',
      '15 users',
      '50 due-diligence reports / mo',
      'High-volume auto-battlecards (fair use)',
      'Due-diligence + battlecard PDF',
      'API access (50K req/day)',
    ],
    ctaLabel: 'Start trial',
  },
  {
    name: 'Scale',
    slug: 'scale',
    monthly: 2499,
    annualPerMonth: 2083,
    annualTotal: 24990,
    tagline: 'For organizations operationalizing competitive intelligence.',
    features: [
      'Everything in Business',
      '500 tracked companies',
      '50 users',
      'Audit log',
      'Unlimited due-diligence reports',
      'Unlimited auto-battlecards (fair use)',
      'API access (200K req/day)',
    ],
    ctaLabel: 'Start trial',
  },
]

const FAQS: { q: string; a: string }[] = [
  {
    q: 'How is this different from Crunchbase or G2?',
    a: 'Crunchbase reports funding once a quarter. G2 ranks. PulseSignal watches ten signal modules every day (pricing, leadership, hiring, API and integrations, product launches, customer logos, customer sentiment, tech stack, positioning, funding), then reads across all ten with acquisition scoring and due-diligence workflows, and sends you the changes the day they happen. Different problem, different speed.',
  },
  {
    q: 'Where does the data come from?',
    a: 'Public, terms-of-service-friendly sources only: pricing pages, public changelogs, ATS feeds like Greenhouse and Lever, SEC EDGAR filings, UK Companies House, Trustpilot and public app stores, public funding databases, and the Wayback Machine. The complete list lives on our sub-processor page. We do NOT pull from LinkedIn, Crunchbase, or any source whose terms restrict indexing.',
  },
  {
    q: 'Will I get spammed with alerts?',
    a: 'No. Severity is the whole product. Your daily digest groups info-level signals; only critical and notable signals page you. You can pattern-block (e.g. silence "PRICING.REGION") in one click.',
  },
  {
    q: 'Can I export to my CRM, Slack, or Notion?',
    a: 'Slack, Microsoft Teams, and Telegram are first-party from Pro, and so are custom alert rules and webhooks. The full API is on Pro and above too, so you can push signals into HubSpot, Salesforce, or Notion by webhook or our Zapier, Make.com, and n8n walkthroughs.',
  },
  {
    q: 'How fresh is the data?',
    a: 'Every tracked company is refreshed every day (one full cycle at 02:00 UTC). Critical and notable signals are dispatched the moment a cycle finishes, and the daily digest is emailed once the cycle completes.',
  },
  {
    q: 'How long until I see value?',
    a: 'A single missed pricing change can pay for the year. Every plan starts with a 14-day free trial. We collect a card at signup and the first charge lands on day 15, so you can validate before you commit. Annual is the default and works out to two months free versus paying monthly.',
  },
  {
    q: 'Can I cancel or get a refund?',
    a: 'Cancel anytime, in one click, from your billing settings. If you cancel during the 14-day trial you are never charged. After that, we offer a 7-day no-questions refund on your first charge: email us within 7 days and we send the money back, no forms and no retention call. Every email reaches us directly.',
  },
  {
    q: 'Do you offer team seats?',
    a: 'Starter includes 1 user, Pro 5 users, Business 15 users, Scale 50 users. Seat counts are fixed per plan, upgrade for more.',
  },
  {
    q: 'What about API access?',
    a: 'Pro: 10,000 requests per day. Business: 50,000 per day. Scale: 200,000 per day. All expose the full signal stream, search, and export endpoints. All exposed over a documented REST API.',
  },
]

const FAQ_PAGE_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.a,
    },
  })),
}
